import { fetchJobGrades } from '../api/hr-job-grades';
import { registerSessionCache } from './session-caches';
import { UNKNOWN_LEVEL } from './people-order';

/**
 * 직위 이름 → 급 — **한 번만 읽고 나눠 쓴다** (2026-08-31).
 *
 * 사람 목록을 「직책 → 직위」로 세우려면(`people-order`) 직위의 **서열**이 있어야 하는데,
 * `users.position` 은 **이름 문자열**뿐이다. 급은 `hr_job_grades` 가 갖고 있다
 * (`level` — 클수록 높다. 대표이사 18 · 이사 13 · 사원 5).
 *
 * ★★ **못 읽어도 화면은 선다.** 이 목록은 담당자만 볼 수 있어(`/hr/job-grades`) 직원이 열면
 *    403 이다. 그때는 **빈 표**로 두고 직위를 「모름」으로 친다 — 정렬이 이름순으로 내려앉을
 *    뿐 목록이 사라지지 않는다. 값을 모른다고 지어내지 않는다.
 * ★ **호칭도 같은 급으로 넣는다** — 「수석보」를 「수석」이라 부르는 회사가 있고(`callName`),
 *   사람의 `position` 에 호칭이 적혀 있을 수 있다.
 * ★ 세션이 끝나면 비운다 — 회사가 다르면 직위표가 다르다(테넌트 경계).
 */
type LevelMap = ReadonlyMap<string, number>;

let cached: Promise<LevelMap> | null = null;

const EMPTY: LevelMap = new Map();

function normalize(name: string): string {
  return name.trim();
}

export function loadJobGradeLevels(): Promise<LevelMap> {
  cached ??= fetchJobGrades()
    .then((board) => {
      const map = new Map<string, number>();
      for (const g of board.grades) {
        if (g.name) map.set(normalize(g.name), g.level);
        // 호칭은 정식 이름을 **덮지 않는다** — 「수석보 → 수석」처럼 둘이 겹칠 수 있다.
        const call = g.callName ? normalize(g.callName) : '';
        if (call && !map.has(call)) map.set(call, g.level);
      }
      return map as LevelMap;
    })
    // 다시 물어볼 수 있게 캐시를 비운다 — 한 번의 실패가 세션 내내 남지 않게.
    .catch(() => { cached = null; return EMPTY; });
  return cached;
}

/** 표를 받은 뒤 쓰는 조회 — 모르면 `UNKNOWN_LEVEL`(맨 아래). */
export function levelOfPosition(levels: LevelMap, position: string | null | undefined): number {
  if (!position) return UNKNOWN_LEVEL;
  return levels.get(normalize(position)) ?? UNKNOWN_LEVEL;
}

/** 테스트 전용 — 모듈 캐시 비우기. */
export function _resetJobGradeLevelsForTests(): void { cached = null; }

registerSessionCache(() => { cached = null; });
